import React from 'react'


// The bar charts draw themselves as they reach the screen: each row's bars
// grow out of nothing, the figure at each bar's end appears where the bar
// arrives, and the row's verdict last.
//
// The drawing itself is done by the stylesheet, as transitions, so it runs
// on the browser's own clock and nothing re-renders while it plays. What is
// decided here is only when each row starts, and it is decided per row:
// every row of a chart is watched, and the ones that come into view together
// start one after the other, top to bottom, a beat apart. On a wide screen
// the whole chart is in view at once and runs top to bottom as one; on a
// phone the rows come in as the reader scrolls and each starts as it does.
// A row started gets --delay, its beat, and data-revealed, which the
// stylesheet transitions on.
//
// The pace is the chart's: the stylesheet gives every bar a time in
// proportion to its length (--share times --speed, the time of a full-length
// bar), so the two bars of a row advance as one front and the shorter one
// stops first. --speed is set per group of rows, so the formats of the
// benchmark do not all move at the same pace.


///@brief   The beat between two rows that start together, in milliseconds.
const ROW_STAGGER_MS: number = 110

///@brief   How far into the viewport a row has to be before it starts, so
///         it never draws while still cut by the bottom edge.
const ROOT_MARGIN: string = '0px 0px -10% 0px'


///@brief   Watches the rows of the chart put on the returned ref (the
///         elements matching `rowSelector` under it) and starts each one
///         when it comes into view, rows arriving together a beat apart. A
///         visitor who has asked for less motion, or a browser that cannot
///         watch, gets every bar whole at once.
export function useBarReveal( rowSelector: string ): React.RefObject<HTMLDivElement | null> {

	const ref = React.useRef<HTMLDivElement>( null )

	React.useEffect( (): ( () => void ) => {
		const root: HTMLDivElement | null = ref.current
		if ( root === null ) return (): void => {}

		const rows: HTMLElement[] = Array.from( root.querySelectorAll<HTMLElement>( rowSelector ) )

		const reveal = ( row: HTMLElement, delayMs: number ): void => {
			row.style.setProperty( '--delay', `${ delayMs }ms` )
			row.dataset.revealed = ''
		}

		const bReducedMotion: boolean =
			typeof window.matchMedia === 'function' && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches

		if ( typeof IntersectionObserver !== 'function' || bReducedMotion ) {
			rows.forEach( ( row: HTMLElement ): void => reveal( row, 0 ) )
			return (): void => {}
		}

		const observer = new IntersectionObserver( ( entries: IntersectionObserverEntry[] ): void => {
			// The rows that came into view together, top to bottom.
			const arrived: IntersectionObserverEntry[] = entries
				.filter( ( entry: IntersectionObserverEntry ): boolean => entry.isIntersecting )
				.sort( ( a: IntersectionObserverEntry, b: IntersectionObserverEntry ): number => a.boundingClientRect.top - b.boundingClientRect.top )

			arrived.forEach( ( entry: IntersectionObserverEntry, i: number ): void => {
				reveal( entry.target as HTMLElement, i * ROW_STAGGER_MS )
				observer.unobserve( entry.target )
			} )
		}, { threshold: 0.6, rootMargin: ROOT_MARGIN } )

		rows.forEach( ( row: HTMLElement ): void => observer.observe( row ) )

		return (): void => observer.disconnect()
	}, [ rowSelector ] )

	return ref
}
